<template>
  <div class="py-4 container-fluid">
    <div class="row">
      <div class="col-md-8 mx-auto">
        <div class="card shadow-lg">
          <div class="card-header pb-0">
            <div class="d-flex align-items-center">
              <h6 class="mb-0 ms-3">Finalizar Gestión de Tarea ({{ taskInfo.type_name || 'Cargando...' }})</h6>
            </div>
          </div>

          <div v-if="loadingTask" class="card-body text-center">
            <div class="spinner-border text-success" role="status"></div>
            <p class="mt-2 text-sm text-secondary">Cargando detalles de la tarea...</p>
          </div>

          <div v-else class="card-body">
            <div class="bg-gray-100 p-3 border-radius-lg mb-4">
              <p class="text-xs font-weight-bold text-uppercase mb-1">Tarea a realizar:</p>
              <h6 class="text-sm">{{ taskInfo.description }}</h6>
              <h6 v-if="taskInfo.task_type==1|| taskInfo.task_type==6" class="text-sm">Monto: ${{ taskInfo.to_collect }}</h6>
              <span :class="['badge badge-sm', priorityBadge(taskInfo.priority)]">
                {{ taskInfo.priority }}
              </span>
            </div>

            <form @submit.prevent="submitCompletion">
              <div class="col-md-12 text-center mb-4">
                <label class="form-control-label d-block">Captura de Evidencia (Foto)</label>
                <div
                  class="evidence-upload-area mx-auto"
                  @click="openCamera"
                  :style="evidencePreview ? 'border-style: solid' : ''"
                >
                  <img
                    v-if="evidencePreview"
                    :src="evidencePreview"
                    class="img-fluid border-radius-lg"
                  />
                  <div v-else class="py-4">
                    <i class="fa fa-camera fa-3x text-secondary mb-2"></i>
                    <p class="text-sm mb-0">Tocar para tomar foto</p>
                  </div>
                  <input
                    type="file"
                    ref="cameraInput"
                    accept="image/*"
                    capture="environment"
                    style="display: none"
                    @change="handleFileChange"
                  />
                </div>
                <small v-if="submitted && !assignment.evidence_url" class="text-danger">
                  La foto de evidencia es obligatoria.
                </small>
              </div>

              <div class="col-md-12 mb-3">
                <label class="form-control-label">Notas de la gestión</label>
                <textarea
                  v-model="assignment.user_notes"
                  class="form-control"
                  rows="3"
                  placeholder="Detalles de la gestión..."
                ></textarea>
              </div>

              <template v-if="taskInfo.task_type==1|| taskInfo.task_type==6">
                <div class="col-md-12 mb-3">
                  <label class="form-label mb-1">Forma de Pago</label>
                  <select class="form-select" v-model="assignment.payment_method">
                    <option value="" disabled>Seleccione una opción</option>
                    <option value="Efectivo">Efectivo</option>
                    <option value="Agencia">Cliente pagó en agencia</option>
                  </select>
                </div>

                <div class="col-md-12 mb-3">
                  <label class="form-label mb-1">Monto recuperado</label>
                  <input type="number" step="0.01" class="form-control" v-model.number="assignment.collected" min="0"/>
                </div>
              </template>
              <div class="col-md-12 mb-3">
                <div class="form-check form-switch ps-0">
                  <input class="form-check-input ms-0 me-2" type="checkbox" id="commitmentCheck" v-model="assignment.has_commitment">
                  <label class="form-check-label font-weight-bold" for="commitmentCheck">Programar compromiso de pago</label>
                </div>
              </div>
              <div v-if="assignment.has_commitment" class="col-md-12 mb-4">
                <label class="form-label mb-1">Fecha del Compromiso</label>
                <input type="date" class="form-control" v-model="assignment.commitment_date" :min="todayDate"/>
              </div>
              <div class="col-md-12">
                <div class="d-flex align-items-center bg-light p-3 border-radius-md justify-content-between">
                  <div class="d-flex align-items-center">
                    <i :class="['fa fa-map-marker-alt me-2', isInRange ? 'text-success' : 'text-danger']"></i>
                    <span class="text-xs font-weight-bold">
                      {{ locationStatus }}
                    </span>
                  </div>
                  <button
                    v-if="gpsError"
                    type="button"
                    @click="restartGPS"
                    class="btn btn-sm btn-outline-primary mb-0 py-1 px-2">
                    <i class="fa fa-sync me-1"></i> Reintentar
                  </button>
                </div>
                <div v-if="currentDistance !== null" class="progress mt-2" style="height: 4px;">
                  <div
                    class="progress-bar"
                    :class="isInRange ? 'bg-success' : 'bg-danger'"
                    :style="{ width: Math.min((100 / (currentDistance / 100)), 100) + '%' }">
                  </div>
                </div>
              </div>

              <argon-button
                type="submit"
                class="mt-4"
                :color="isInRange ? 'success' : 'secondary'"
                fullWidth
                size="lg"
                :disabled="isSubmitting || (taskInfo.tlatitude && !isInRange)"
              >
                <span v-if="isSubmitting">Enviando...</span>
                <span v-else-if="taskInfo.tlatitude && !isInRange">Fuera de Rango</span>
                <span v-else>Confirmar y Finalizar</span>
              </argon-button>
            </form>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import ArgonButton from "@/components/ArgonButton.vue";
import axios from "axios";
import Swal from 'sweetalert2';
import { useUpload } from "@/composables/useUpload";

export default {
  name: "CompleteTask",
  components: { ArgonButton },
  setup() {
    const { uploadFile, uploading } = useUpload();
    return { uploadFile, uploading };
  },
  data() {
    return {
      loadingTask: true,
      isSubmitting: false,
      submitted: false,
      validateProximity: false,
      evidencePreview: null,
      locationStatus: "Iniciando GPS...",
      gpsError: false,
      currentDistance: null,
      watchId: null,
      taskInfo: {},
      assignment: {
        assignment_id: null,
        evidence_url: null,
        user_notes: '',
        latitude: null,
        longitude: null,
        collected: 0,
        payment_method: '', // Nuevo
        has_commitment: false, // Nuevo
        commitment_date: null // Nuevo
      }
    };
  },
  computed: {
    todayDate() {
      // Retorna la fecha de hoy para limitar el input date hacia atrás
      return new Date().toISOString().split('T')[0];
    },
    // Definimos rango de 100 metros por margen de error del sensor
    isInRange() {
      if (!this.validateProximity) return true;
      if (!this.taskInfo.tlatitude || parseFloat(this.taskInfo.tlatitude) === 0) return true;
      if (this.currentDistance === null) return false;
      return this.currentDistance <= 100;
    }
  },
  methods: {
    async fetchTaskDetails() {
      try {
        const token = localStorage.getItem("accessToken");
        const response = await axios.get(`${this.$store.state.apiURL}task/details?id=${this.$route.params.id}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        this.taskInfo = response.data;
        this.assignment.assignment_id = response.data.assignment_id;
        this.loadingTask = false;

        // Iniciar seguimiento GPS después de tener la meta
        this.startLocationTracking();
      } catch (e) {
        Swal.fire('Error', 'No se pudo cargar la tarea', 'error');
      }
    },

    startLocationTracking() {
      if (!navigator.geolocation) {
        this.locationStatus = "GPS no disponible";
        return;
      }

      this.gpsError = false;
      this.locationStatus = "Buscando señal...";

      const geoOptions = {
        enableHighAccuracy: true,
        timeout: 20000, // 20 segundos para evitar el error de timeout rápido
        maximumAge: 10000
      };

      this.watchId = navigator.geolocation.watchPosition(
        (pos) => {
          this.assignment.latitude = pos.coords.latitude;
          this.assignment.longitude = pos.coords.longitude;
          this.gpsError = false;

          if (this.taskInfo.tlatitude && parseFloat(this.taskInfo.tlatitude) !== 0) {
            this.currentDistance = this.calculateDistance(
              pos.coords.latitude,
              pos.coords.longitude,
              parseFloat(this.taskInfo.tlatitude),
              parseFloat(this.taskInfo.tlongitude)
            );

            this.locationStatus = this.isInRange
              ? `📍 En rango (${Math.round(this.currentDistance)}m)`
              : `📍 A ${Math.round(this.currentDistance)}m (Acércate)`;
          } else {
            this.locationStatus = "📍 Ubicación lista";
          }
        },
        (err) => {
          this.gpsError = true;
          if (err.code === 3) {
            this.locationStatus = "⚠️ Tiempo de espera agotado. Reintenta.";
          } else {
            this.locationStatus = "⚠️ Error GPS: " + err.message;
          }
        },
        geoOptions
      );
    },

    restartGPS() {
      if (this.watchId) navigator.geolocation.clearWatch(this.watchId);
      this.startLocationTracking();
    },

    calculateDistance(lat1, lon1, lat2, lon2) {
      const R = 6371e3;
      const φ1 = lat1 * Math.PI / 180;
      const φ2 = lat2 * Math.PI / 180;
      const Δφ = (lat2 - lat1) * Math.PI / 180;
      const Δλ = (lon2 - lon1) * Math.PI / 180;
      const a = Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
                Math.cos(φ1) * Math.cos(φ2) *
                Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
      const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
      return R * c;
    },

    openCamera() {
      this.$refs.cameraInput.click();
    },

    async handleFileChange(event) {
      const file = event.target.files[0];
      if (!file) return;
      const compressedFile = await this.compressImage(file, {
        maxWidth: 1024,
        quality: 0.7
      });
      this.evidencePreview = URL.createObjectURL(compressedFile);

      try {
        const url = await this.uploadFile(compressedFile);
        if (url) {
          this.assignment.evidence_url = url;
        }
      } catch (e) {
        Swal.fire('Error', 'No se pudo subir la imagen', 'error');
      }
    },

    priorityBadge(p) {
      const maps = { 'High': 'badge-danger', 'Medium': 'badge-warning' };
      return maps[p] || 'badge-info';
    },

    async submitCompletion() {
      this.submitted = true;

      // VALIDACIÓN 1: FOTO
      if (!this.assignment.evidence_url) {
        Swal.fire('Foto requerida', 'Debes subir una evidencia', 'warning');
        return;
      }

      // VALIDACIÓN 2: RANGO
      if (!this.isInRange) {
        Swal.fire('Fuera de rango', `Estás a ${Math.round(this.currentDistance)}m. Debes estar a menos de 100m.`, 'error');
        return;
      }

      const isCollectionTask = this.taskInfo.task_type == 1 || this.taskInfo.task_type == 6;

      // VALIDACIÓN 3: FORMA DE PAGO SI HAY COBRO
      if (isCollectionTask && this.assignment.collected > 0 && !this.assignment.payment_method) {
        Swal.fire('Forma de pago requerida', 'Debes indicar si el pago fue en Efectivo o en Agencia.', 'warning');
        return;
      }

      // VALIDACIÓN 4: FECHA DE COMPROMISO
      if (this.assignment.has_commitment && !this.assignment.commitment_date) {
        Swal.fire('Fecha requerida', 'Selecciona la fecha para el compromiso de pago.', 'warning');
        return;
      }

      // ---------------------------------------------------------------------
      // CONFIRMACIÓN DE CULMINACIÓN (SWEETALERT)
      // ---------------------------------------------------------------------
      if (isCollectionTask && this.assignment.collected > 0) {
        // Confirmación Especial para dinero recuperado
        const { value: confirmAmount } = await Swal.fire({
          title: 'Confirmación de Cobro',
          text: `Se reportará un pago por ${this.assignment.payment_method}. Digita el monto recuperado para confirmar:`,
          icon: 'warning',
          input: 'number',
          inputAttributes: {
            step: '0.01',
            min: '0'
          },
          customClass: {
            input: 'swal-input-full text-center'
          },
          showCancelButton: true,
          confirmButtonColor: '#FF8000',
          cancelButtonColor: '#f5365c',
          confirmButtonText: 'Confirmar Monto',
          cancelButtonText: 'Cancelar',
          didOpen: () => {
            const input = Swal.getInput();
            input.style.width = '90%';
            input.style.maxWidth = '100%';
            input.style.margin = '1em auto';
            input.style.textAlign = 'center';
          },
          inputValidator: (value) => {
            if (!value) {
              return 'Debes escribir el monto para continuar';
            }
          }
        });

        if (!confirmAmount) return; // Si cancela el modal

        // Verificamos que el monto digitado coincida con lo del formulario
        if (parseFloat(confirmAmount) !== parseFloat(this.assignment.collected)) {
          Swal.fire('Discrepancia de Monto', 'El monto digitado no coincide con el formulario. Por favor verifica para evitar descuadres.', 'error');
          return;
        }

      } else {
        // Confirmación estándar para tareas normales o cobros en 0
        const { isConfirmed } = await Swal.fire({
          title: '¿Finalizar Gestión?',
          text: 'Se enviará el reporte de esta visita a la central.',
          icon: 'question',
          showCancelButton: true,
          confirmButtonColor: '#FF8000',
          cancelButtonColor: '#f5365c',
          confirmButtonText: 'Sí, finalizar',
          cancelButtonText: 'Revisar'
        });

        if (!isConfirmed) return;
      }
      // ---------------------------------------------------------------------

      // LÓGICA EXISTENTE DE ENVÍO DE DATOS
      this.isSubmitting = true;
      const token = localStorage.getItem("accessToken");

      try {
        await axios.post(`${this.$store.state.apiURL}task/transaction/`, this.assignment, {
          headers: { 'Authorization': `Bearer ${token}` }
        });

        if (this.watchId) navigator.geolocation.clearWatch(this.watchId);

        let successMsg = 'Gestión guardada con éxito';
        if (this.assignment.has_commitment) {
          successMsg += ' y compromiso programado.';
        }

        Swal.fire('Completado', successMsg, 'success');
        this.$router.push('/tasks/my');
      } catch (e) {
        Swal.fire('Error', 'No se pudo finalizar la tarea', 'error');
      } finally {
        this.isSubmitting = false;
      }
    },

    compressImage(file, { maxWidth, quality }) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = (event) => {
          const img = new Image();
          img.src = event.target.result;
          img.onload = () => {
            const canvas = document.createElement('canvas');
            let width = img.width;
            let height = img.height;

            // Calcular nuevas dimensiones manteniendo aspecto
            if (width > maxWidth) {
              height = (maxWidth / width) * height;
              width = maxWidth;
            }

            canvas.width = width;
            canvas.height = height;

            const ctx = canvas.getContext('2d');
            ctx.drawImage(img, 0, 0, width, height);

            // Convertir a Blob (formato JPG para mejor compresión)
            canvas.toBlob((blob) => {
              if (blob) {
                // Convertir blob de nuevo a File para que el backend lo reciba igual
                const compressedFile = new File([blob], file.name, {
                  type: 'image/jpeg',
                  lastModified: Date.now(),
                });
                resolve(compressedFile);
              } else {
                reject(new Error('Canvas toBlob failed'));
              }
            }, 'image/jpeg', quality);
          };
          img.onerror = (err) => reject(err);
        };
        reader.onerror = (err) => reject(err);
      });
    },
  },
  mounted() {
    this.fetchTaskDetails();
  },
  beforeUnmount() {
    if (this.watchId) navigator.geolocation.clearWatch(this.watchId);
  }
};
</script>

<style scoped>
.evidence-upload-area {
  width: 100%;
  max-width: 300px;
  min-height: 200px;
  border: 3px dashed #dee2e6;
  border-radius: 15px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  background-color: #f8f9fa;
  transition: all 0.3s ease;
  overflow: hidden;
}
.evidence-upload-area:hover {
  border-color: #FF8000;
  background-color: #eefdf5;
}
.swal-input-full {
  width: 100% !important;
  max-width: 100% !important;
  margin-left: auto !important;
  margin-right: auto !important;
}

.swal2-input{
  max-width: 100% !important;
  width: 100% !important;
}
.swal2-input[type=number] {
  max-width: 100% !important;
  width: 100% !important;
}
</style>